import React, { useState } from 'react';
import LocalizedLink from '../LocalizedLink';
import { getIcon, productColors } from '../icons/lineIcons';

// Building blocks for the corporate pages (home, telecom, platform, etc.).
// Content lives in src/data/pages/*.js, one object per locale.

const isExternal = (to) => /^(https?:|mailto:|tel:)/.test(to);

export const Button = ({ label, to, style = 'primary' }) => {
  const className = `btn-landing btn-${style} btn-large`;
  if (isExternal(to)) {
    return <a href={to} className={className}>{label}</a>;
  }
  return <LocalizedLink to={to} className={className}>{label}</LocalizedLink>;
};

const Buttons = ({ ctas, className }) => (
  <div className={className}>
    {ctas.map((cta) => <Button key={cta.label} {...cta} />)}
  </div>
);

// Screenshot available in a light and a dark version: `${src}-light.jpg` / `${src}-dark.jpg`.
// CSS shows the one matching the current theme; lazy loading skips the hidden one.
export const ThemedImage = ({ src, alt }) => (
  <>
    <img className="themed-light" src={`${src}-light.jpg`} alt={alt} loading="lazy" width="1400" height="875" />
    <img className="themed-dark" src={`${src}-dark.jpg`} alt={alt} loading="lazy" width="1400" height="875" />
  </>
);

// Visible placeholder for content TriDyme still has to provide
export const Todo = ({ children }) => (
  <div className="todo-box" role="note">
    <strong>TODO</strong> {children}
  </div>
);

export const Hero = ({ badge, title, highlight, titleEnd, subtitle, ctas = [], stats = [], image }) => (
  <section className="hero-landing hero-single">
    <div className="hero-bg"></div>
    <div className="hero-grid"></div>
    <div className="hero-container">
      <div className="hero-content">
        {badge && (
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            <span>{badge}</span>
          </div>
        )}
        <h1>
          {title}
          {highlight && <span className="highlight">{highlight}</span>}
          {titleEnd}
        </h1>
        {subtitle && <p className="hero-subtitle">{subtitle}</p>}
        {ctas.length > 0 && <Buttons ctas={ctas} className="hero-cta" />}
        {stats.length > 0 && (
          <div className="hero-stats">
            {stats.map((stat) => (
              <div key={stat.label} className="stat">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {image && (
        <figure className="hero-shot">
          <ThemedImage src={image.src} alt={image.alt} />
        </figure>
      )}
    </div>
  </section>
);

export const Section = ({ id, tag, title, subtitle, tone = 'white', children }) => (
  <section id={id} className={`corp-section corp-section-${tone}`}>
    <div className="corp-container">
      {(tag || title || subtitle) && (
        <div className="section-header">
          {tag && <span className="section-tag">{tag}</span>}
          {title && <h2 className="section-title">{title}</h2>}
          {subtitle && <p className="section-subtitle">{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  </section>
);

export const Cards = ({ items, columns = 3 }) => (
  <div className={`corp-cards corp-cards-${columns}`}>
    {items.map((item) => (
      <div key={item.title} className="corp-card">
        {item.icon && (
          <div
            className={`feature-icon ${productColors[item.icon] ? 'product-icon' : ''}`}
            style={productColors[item.icon] ? { '--product-color': productColors[item.icon] } : undefined}
            aria-hidden="true"
          >
            {getIcon(item.icon)}
          </div>
        )}
        {item.logo && (
          <div className="corp-card-logo"><img src={item.logo.src} alt={item.logo.name} loading="lazy" /></div>
        )}
        {item.kicker && <span className="corp-card-kicker">{item.kicker}</span>}
        <h3>{item.title}</h3>
        {item.text && <p>{item.text}</p>}
        {item.list && (
          <ul className="corp-list">
            {item.list.map((line) => <li key={line}>{line}</li>)}
          </ul>
        )}
        {item.todo && <Todo>{item.todo}</Todo>}
        {item.to && (
          <LocalizedLink to={item.to} className="corp-card-link">
            {item.linkLabel} →
          </LocalizedLink>
        )}
      </div>
    ))}
  </div>
);

export const Steps = ({ items }) => (
  <ol className="corp-steps">
    {items.map((item, index) => (
      <li key={item.title} className="corp-step">
        <span className="corp-step-number">{index + 1}</span>
        <div>
          {item.kicker && <span className="corp-card-kicker">{item.kicker}</span>}
          <h3>{item.title}</h3>
          <p>{item.text}</p>
        </div>
      </li>
    ))}
  </ol>
);

export const Stats = ({ items, note }) => (
  <>
    <div className="corp-stats">
      {items.map((item) => (
        <div key={item.label} className="corp-stat">
          <span className="corp-stat-value">{item.value}</span>
          <span className="corp-stat-label">{item.label}</span>
        </div>
      ))}
    </div>
    {note && <p className="corp-note">{note}</p>}
  </>
);

export const Table = ({ headers, rows }) => (
  <div className="corp-table-wrapper">
    <table className="corp-table">
      <thead>
        <tr>{headers.map((header) => <th key={header} scope="col">{header}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[0]}>
            {row.map((cell, index) => (index === 0
              ? <th key={cell} scope="row">{cell}</th>
              : <td key={cell}>{cell}</td>))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Product screenshots with captions
export const Screens = ({ items, columns = 2 }) => (
  <div className={`corp-screens corp-cards-${columns}`}>
    {items.map((item) => (
      <figure key={item.src} className="corp-screen">
        <div className="corp-screen-frame">
          <ThemedImage src={item.src} alt={item.alt} />
        </div>
        <figcaption>
          {item.title && <strong>{item.title}</strong>}
          {item.caption && <span>{item.caption}</span>}
        </figcaption>
      </figure>
    ))}
  </div>
);

export const Quote = ({ children }) => <p className="corp-quote">{children}</p>;

export const CTA = ({ title, text, ctas = [] }) => (
  <section className="cta-section">
    <div className="cta-container">
      <h2>{title}</h2>
      {text && <p>{text}</p>}
      {ctas.length > 0 && <Buttons ctas={ctas} className="cta-buttons" />}
    </div>
  </section>
);

// Business objects of an industry ontology, shown as chips
export const Chips = ({ items }) => (
  <ul className="corp-chips">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

// Use cases grouped by business function, one tab at a time (Palantir-style)
export const Tabs = ({ items }) => {
  const [active, setActive] = useState(0);
  const current = items[active];
  return (
    <div className="corp-tabs">
      <div className="corp-tabs-list" role="tablist">
        {items.map((item, index) => (
          <button
            key={item.label}
            type="button"
            role="tab"
            aria-selected={index === active}
            className={index === active ? 'active' : ''}
            onClick={() => setActive(index)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="corp-tabs-panel" role="tabpanel">
        <p className="corp-tabs-intro">{current.text}</p>
        <Cards items={current.items} columns={current.items.length > 3 ? 4 : 3} />
        {current.to && (
          <LocalizedLink to={current.to} className="corp-card-link">{current.linkLabel} →</LocalizedLink>
        )}
      </div>
    </div>
  );
};

// Featured customer story (Shapemaker-style): context, metrics, link
export const Story = ({ logo, kicker, title, text, list, metrics = [], note, todo, to, linkLabel }) => (
  <article className="corp-story">
    <div className="corp-story-text">
      {logo && <div className="corp-card-logo"><img src={logo.src} alt={logo.name} loading="lazy" /></div>}
      {kicker && <span className="corp-card-kicker">{kicker}</span>}
      <h3>{title}</h3>
      <p>{text}</p>
      {list && (
        <ul className="corp-list">
          {list.map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}
      {todo && <Todo>{todo}</Todo>}
      {to && <LocalizedLink to={to} className="corp-card-link">{linkLabel} →</LocalizedLink>}
    </div>
    <div className="corp-story-metrics">
      {metrics.map((metric) => (
        <div key={metric.label} className="corp-story-metric">
          <span className="corp-stat-value">{metric.value}</span>
          <span className="corp-stat-label">{metric.label}</span>
        </div>
      ))}
      {note && <p className="corp-note">{note}</p>}
    </div>
  </article>
);

// Customer quotes. Only real, approved quotes may be published: empty slots render as TODO.
export const Quotes = ({ items }) => (
  <div className="corp-cards corp-cards-3">
    {items.map((item, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <figure key={index} className="corp-card corp-quote-card">
        {item.quote ? (
          <>
            <blockquote>« {item.quote} »</blockquote>
            <figcaption><strong>{item.author}</strong><span>{item.role}</span></figcaption>
          </>
        ) : (
          <Todo>{item.todo}</Todo>
        )}
      </figure>
    ))}
  </div>
);

// Index of industries (Palantir offerings style)
export const Offerings = ({ items }) => (
  <ul className="corp-offerings">
    {items.map((item) => (
      <li key={item.title}>
        <LocalizedLink to={item.to} className="corp-offering">
          <span className="corp-offering-title">↳ {item.title}</span>
          <span className="corp-offering-text">{item.text}</span>
          <span className="corp-offering-link">{item.linkLabel} →</span>
        </LocalizedLink>
      </li>
    ))}
  </ul>
);

// Client logos on uniform white tiles (readable in both themes)
export const Logos = ({ items, note }) => (
  <>
    <ul className="corp-logos">
      {items.map((item) => (
        <li key={item.name} className="corp-logo">
          <img src={item.src} alt={item.name} loading="lazy" />
        </li>
      ))}
    </ul>
    {note && <p className="corp-note">{note}</p>}
  </>
);
